import type { FundActivity } from "../../../functions/src/types";
import { formatOrderDayLabel } from "../../../functions/src/api/dates";
import { getLangValue, type Language } from "../../api/Lang";
import type { WalletHistoryStrings } from "./wallet-history-strings";

/** Backfill may write types not yet on {@link FundActivity}. */
type DisplayActivity = FundActivity | { type: "legacyOrderRefund" };

export function formatFundActivityLabel(
    activity: DisplayActivity,
    strings: WalletHistoryStrings,
    lang: Language
): string {
    switch (activity.type) {
        case "topUp":
            return strings.topUp;
        case "placeOrder":
            return strings.placeOrder(formatOrderDayLabel(activity.orderDate, lang));
        case "changeOrderRefund":
            return strings.changeOrderRefund(formatOrderDayLabel(activity.orderDate, lang));
        case "cancelOrderRefund":
            return strings.cancelOrderRefund(formatOrderDayLabel(activity.orderDate, lang));
        case "placePackage": {
            const title =
                (activity.packageTitle && getLangValue(activity.packageTitle, lang)) ||
                activity.packageId;
            return title ? strings.placePackage(title) : strings.placePackageGeneric;
        }
        case "adminCancelOrder":
            return strings.adminCancelOrder(formatOrderDayLabel(activity.orderDate, lang));
        case "adminCancelOrderRecipient":
            return strings.adminCancelOrderRecipient(formatOrderDayLabel(activity.orderDate, lang));
        case "adminAdjustment":
            return activity.note ? strings.adminAdjustmentNote(activity.note) : strings.adminAdjustment;
        case "winbankTopUp":
            return strings.winbankTopUp;
        case "legacyOrderRefund":
            return strings.legacyOrderRefund;
        default:
            return strings.legacyBalanceChange;
    }
}

/** Subtitle under the badge (order date, cancellation context, package title, etc.). */
export function formatFundActivityDetail(
    activity: DisplayActivity,
    strings: WalletHistoryStrings,
    lang: Language
): string | undefined {
    switch (activity.type) {
        case "topUp":
            return strings.topUp;
        case "placeOrder":
            return strings.orderForDate(formatOrderDayLabel(activity.orderDate, lang));
        case "changeOrderRefund":
            return strings.changeOrderRefundDetail(formatOrderDayLabel(activity.orderDate, lang));
        case "cancelOrderRefund":
            return strings.cancelOrderRefundDetail(formatOrderDayLabel(activity.orderDate, lang));
        case "placePackage": {
            const title =
                (activity.packageTitle && getLangValue(activity.packageTitle, lang)) ||
                activity.packageId;
            return title ? strings.placePackage(title) : strings.placePackageGeneric;
        }
        case "adminCancelOrder":
            return strings.adminCancelOrder(formatOrderDayLabel(activity.orderDate, lang));
        case "adminCancelOrderRecipient":
            return strings.adminCancelOrderRecipient(formatOrderDayLabel(activity.orderDate, lang));
        case "adminAdjustment":
            return activity.note ? strings.adminAdjustmentNote(activity.note) : strings.adminAdjustment;
        case "winbankTopUp":
            return strings.winbankTopUp;
        case "legacyOrderRefund":
            return strings.legacyOrderRefund;
        default:
            return undefined;
    }
}

export type WalletHistoryBadgeVariant = "credit" | "debit";

export function getWalletHistoryBadgeVariant(amount: number): WalletHistoryBadgeVariant {
    return amount >= 0 ? "credit" : "debit";
}

export function getWalletHistoryBadgeLabel(
    amount: number,
    strings: WalletHistoryStrings
): string {
    return amount >= 0 ? strings.badgeRefund : strings.badgeOrder;
}
